import showToast from "hooks/useCustomToast";
import { MemoryFactProto } from "build-ts/memory";

export const listMemoryFacts = async (analystId: string): Promise<MemoryFactProto[]> => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_ANALYST_MEMORY}?analyst_id=${encodeURIComponent(analystId)}`,
      { credentials: "include" }
    );
    if (!response.ok) {
      if (response.status === 404) {
        showToast("Memory facts not found for this analyst", "", "info");
        return [];
      }
      showToast("Failed to load memory facts", "", "error");
      return [];
    }
    const data = await response.json();
    return data.map((fact: MemoryFactProto) => MemoryFactProto.fromJSON(fact));
  } catch (error) {
    showToast("Failed to load memory facts", "", "error");
    return [];
  }
};

/**
 * Create or update a memory fact via a single PUT endpoint.
 * - No `id` on the proto → create (backend generates the ID)
 * - `id` present        → update the existing fact
 *
 * userId identifies who is performing the operation and is stored as
 * added_by on create. It may differ from the original creator on update.
 */
export const saveMemoryFact = async (
  analystId: string,
  userId: string,
  fact: MemoryFactProto,
): Promise<MemoryFactProto | null> => {
  try {
    const url = `${process.env.REACT_APP_ANALYST_MEMORY}?analyst_id=${encodeURIComponent(analystId)}&user_id=${encodeURIComponent(userId)}`;
    const response = await fetch(url, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(MemoryFactProto.toJSON(fact)),
    });
    if (!response.ok) {
      showToast(fact.id ? "Failed to update memory fact" : "Failed to create memory fact", "", "error");
      return null;
    }
    const data = await response.json();
    return MemoryFactProto.fromJSON(data);
  } catch (error) {
    showToast(fact.id ? "Failed to update memory fact" : "Failed to create memory fact", "", "error");
    return null;
  }
};

/**
 * Clone all memory facts from one analyst to another.
 * Each cloned fact gets a new ID and creation timestamp; all other fields are preserved.
 */
export const cloneAnalystMemoryFacts = async (
  sourceAnalystId: string,
  targetAnalystId: string,
): Promise<MemoryFactProto[]> => {
  try {
    const params = new URLSearchParams();
    params.append("source_analyst_id", sourceAnalystId);
    params.append("target_analyst_id", targetAnalystId);
    const url = `${process.env.REACT_APP_CLONE_ANALYST_MEMORY}?${params.toString()}`;
    const response = await fetch(url, {
      method: "POST",
      credentials: "include",
    });
    if (!response.ok) {
      showToast("Failed to clone memory facts", "", "error");
      return [];
    }
    const data = await response.json();
    return data.map((fact: MemoryFactProto) => MemoryFactProto.fromJSON(fact));
  } catch (error) {
    showToast("Failed to clone memory facts", "", "error");
    return [];
  }
};

export const deleteMemoryFact = async (factId: string): Promise<boolean> => {
  try {
    const response = await fetch(`${process.env.REACT_APP_ANALYST_MEMORY}/${factId}`, {
      method: "DELETE",
      credentials: "include",
    });
    if (response.status === 204) return true;
    showToast("Failed to delete memory fact", "", "error");
    return false;
  } catch {
    showToast("Failed to delete memory fact", "", "error");
    return false;
  }
};
